import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import debounce from 'debounce';
import { resolvedEnvironment } from 'services/environment';
import TextField from 'components/lib/TextField/TextField';
import { useLoadGoogleMaps } from 'hooks/useLoadGoogleMaps';
import './AutoSuggestAddress.scss';

type Prediction = google.maps.places.AutocompletePrediction;
type AutocompletionRequest = google.maps.places.AutocompletionRequest;
type PlacesServiceStatus = google.maps.places.PlacesServiceStatus;

interface AddressFields {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}
type PlaceResult = google.maps.GeocoderResult;
type AddressComponent = google.maps.GeocoderAddressComponent;

type AutoSuggestAddressProps = TextFieldProps & {
  name: string;
  debounceTimeMs?: number;
  onAddressSelect?: (address: AddressFields) => void;
};

const parseAddressComponents = (place: PlaceResult): AddressFields => {
  const comps: { [key: string]: string } = {};
  place.address_components.forEach((component: AddressComponent) => {
    const { types } = component;
    if (types.includes('street_number')) {
      comps.street_number = component.long_name;
    }
    if (types.includes('route')) {
      comps.route = component.long_name;
    }
    if (types.includes('locality')) {
      comps.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      comps.state = component.short_name;
    }
    if (types.includes('country')) {
      comps.country = component.long_name;
    }
    if (types.includes('postal_code')) {
      comps.zip = component.long_name;
    }
  });

  return {
    street: [comps.street_number, comps.route].filter(Boolean).join(' '),
    city: comps.city || '',
    state: comps.state || '',
    zip: comps.zip || '',
    country: comps.country || ''
  };
};

const AutoSuggestAddress: React.FC<AutoSuggestAddressProps> = (props) => {
  const {
    name,
    onChange,
    onBlur,
    value,
    debounceTimeMs = 100,
    onAddressSelect,
    ...rest
  } = props;
  const apiKey = resolvedEnvironment.googleApiKey;
  const loaded = useLoadGoogleMaps(apiKey || '');

  const [suggestions, setSuggestions] = useState<Prediction[]>([]);
  const [focused, setFocused] = useState(false);
  const [selectedOnce, setSelectedOnce] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);

  const getPlacePredictionsDebounced = useMemo(
    () =>
      loaded
        ? debounce(
            (
              options: AutocompletionRequest,
              cb: (p: Prediction[] | null, s: PlacesServiceStatus) => void
            ) => {
              new window.google.maps.places.AutocompleteService().getPlacePredictions(
                options,
                cb
              );
            },
            debounceTimeMs
          )
        : null,
    [loaded, debounceTimeMs]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loaded || !value) {
      setSuggestions([]);
      return;
    }
    if (getPlacePredictionsDebounced && focused) {
      getPlacePredictionsDebounced(
        {
          input: String(value),
          types: ['address']
        },
        (preds) => {
          setSuggestions(preds || []);
        }
      );
    }
  }, [getPlacePredictionsDebounced, value, loaded, focused]);

  const onSelect = async (place: any) => {
    if (!place?.place_id) return;

    // Use Google geocoder to get address components via place_id
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const address = parseAddressComponents(results[0]);
        setSelectedOnce(address.street);
        onChange?.({
          target: {
            value: address.street,
            name
          }
        } as React.ChangeEvent<HTMLInputElement>);
        onAddressSelect?.(address);
      }
    });
  };

  const handleSelect = (suggestion: Prediction) => {
    onSelect(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!focused) return;
    if (e.key === 'ArrowDown') {
      setActiveIdx((idx) => Math.min(idx + 1, suggestions.length - 1));
      setSelectedOnce('');
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setActiveIdx((idx) => Math.max(idx - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        handleSelect(suggestions[activeIdx]);
        e.preventDefault();
      }
    }
  };

  return (
    <Box className="AutoSuggestAddress" ref={containerRef}>
      <TextField
        name={name}
        value={value}
        placeholder="Enter your address"
        onKeyDown={handleKeyDown}
        onChange={(e) => {
          onChange?.(e);
          setSelectedOnce('');
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          setSelectedOnce('');
          onBlur?.(e);
        }}
        {...rest}
      />
      {focused && !!suggestions.length && selectedOnce !== value && (
        <ul role="listbox">
          {suggestions.map((s, idx) => (
            <li
              key={s.place_id}
              onMouseDown={() => handleSelect(s)}
              onMouseEnter={() => setActiveIdx(idx)}
              role="option"
              aria-selected={idx === activeIdx}
            >
              {s.description}
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

export default AutoSuggestAddress;
